import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import { Auth } from 'aws-amplify';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import { validateEmail } from '../helpers/validators';
import LoaderButton from '../components/LoaderButton';
import ConfirmationForm from '../components/ConfirmationForm';
import ContentHeader from '../components/ContentHeader';
import globalStyles from '../styles';
import PasswordResetConfirmationForm from '../components/PasswordResetConfirmationForm';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '800px',
        margin: '40px auto',
        paddingBottom: '20px'
    },
    input: {
        margin: '0 20px 10px'
    },
    button: {
        width: '200px',
        height: '56px',
        marginBottom: '30px'
    },
    error: {
        margin: '0 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    register: {
        fontSize: '1em',
        marginBottom: '2em'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold'
    }
});

class Login extends Component {
    constructor(props) {
        super(props);

        this.state = {
            email: '',
            password: '',
            error: '',
            loading: false,
            showConfirmation: false,
            showPasswordReset: false
        };
    }

    getRegisterLink = () => {
        const { props } = this;
        const qs = queryString.parse(props.location.search, {
            ignoreQueryPrefix: true
        });

        if ('redirect' in qs) {
            return `/register?redirect=${qs.redirect}`;
        }

        return `/register`;
    };

    getForgotPasswordLink = () => {
        const { props } = this;
        const qs = queryString.parse(props.location.search, {
            ignoreQueryPrefix: true
        });

        if ('redirect' in qs) {
            return `/forgot-password?redirect=${qs.redirect}`;
        }

        return `/forgot-password`;
    };

    handleChange = event => {
        this.setState({
            [event.target.id]: event.target.value,
            error: false
        });
    };

    handleSubmit = async (event, retryIfQuotaExceeded = true) => {
        event.preventDefault();
        const { email, password } = this.state;
        const { handleAuth } = this.props;

        this.setState({ loading: true });

        try {
            await Auth.signIn(email.toLowerCase(), password);
            handleAuth(true);
        } catch (e) {
            if (e.name === 'QuotaExceededError' && retryIfQuotaExceeded) {
                window.localStorage.clear();

                await this.handleSubmit(event, false);
                return;
            }

            if (e.code === 'PasswordResetRequiredException') {
                this.setState({
                    showPasswordReset: true,
                    loading: false
                });
            }

            if (e.code === 'UserNotConfirmedException') {
                // @FIXME: handle "LimitExceededException" on resendSignUp
                let codeLastSent = '';

                try {
                    codeLastSent = window.localStorage.getItem(
                        'confirmation_code_sent'
                    );
                } catch (localStorageErr) {
                    console.error(localStorageErr);
                }

                if (
                    !codeLastSent ||
                    moment(codeLastSent) < moment().subtract(18, 'hours')
                ) {
                    await Auth.resendSignUp(email.toLowerCase());
                    try {
                        window.localStorage.setItem(
                            'confirmation_code_sent',
                            new Date().toISOString()
                        );
                    } catch (localStorageErr) {
                        console.error(localStorageErr);
                    }
                }

                this.setState({
                    showConfirmation: true
                });
            }
            this.setState({
                error: e.message,
                loading: false
            });
        }
    };

    validateForm() {
        const { email, password } = this.state;

        return validateEmail(email) && password.length > 0;
    }

    renderLoginForm() {
        const { classes } = this.props;
        const { email, password, error, loading } = this.state;

        return (
            <div className={classes.root}>
                <ContentHeader text="Login" id="login-header" />
                <form
                    id="login"
                    className={classes.form}
                    onSubmit={this.handleSubmit}
                >
                    <Typography variant="body1" className={classes.register}>
                        Don&apos;t have a ShuttleID account?{' '}
                        <Link
                            className={classes.link}
                            to={this.getRegisterLink()}
                        >
                            Register
                        </Link>
                        .
                    </Typography>
                    <TextField
                        autoFocus
                        autoComplete="email"
                        className={`${classes.input} input`}
                        id="email"
                        type="email"
                        label="Email"
                        value={email}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <TextField
                        autoComplete="password"
                        className={classes.input}
                        id="password"
                        type="password"
                        label="Password"
                        value={password}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <Typography className={classes.error}>{error}</Typography>
                    <div className={classes.buttons}>
                        <LoaderButton
                            variant="outlined"
                            color="primary"
                            disabled={!this.validateForm()}
                            className={classes.button}
                            type="submit"
                            isLoading={loading}
                            text="Login"
                            loadingText="Logging In..."
                        />
                        <Typography
                            variant="body1"
                            className={classes.register}
                        >
                            <Link
                                className={classes.link}
                                to={this.getForgotPasswordLink()}
                            >
                                Forgot password
                            </Link>
                        </Typography>
                    </div>
                </form>
            </div>
        );
    }

    render() {
        const { handleAuth } = this.props;
        const {
            email,
            password,
            showConfirmation,
            showPasswordReset
        } = this.state;

        if (showConfirmation) {
            return (
                <div className="Login">
                    <ConfirmationForm
                        email={email}
                        password={password}
                        handleAuth={handleAuth}
                    />
                </div>
            );
        }

        if (showPasswordReset) {
            return (
                <div className="Login">
                    <PasswordResetConfirmationForm
                        email={email}
                        handleAuth={handleAuth}
                    />
                </div>
            );
        }

        return <div className="Login">{this.renderLoginForm()}</div>;
    }
}

Login.propTypes = {
    handleAuth: PropTypes.func.isRequired,
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(Login);
